import { ComponentFactory, Injectable, Injector, NgModuleFactoryLoader, NgModuleRef } from '@angular/core';

import { LazyLoadableModuleId } from './lazy-loadable-module-id.enum';
import { LAZY_VIEW_ENTRY_COMPONENT } from './lazy-view-entry-component.injection-token';
import { LazyViewDownloadStrategy } from './load-strategy/lazy-view-download-strategy.service';

@Injectable({
    providedIn: 'root'
})
export class LazyViewLoaderService {
    private readonly ngModuleCache = new Map<LazyLoadableModuleId, NgModuleRef<any>>();

    constructor(
        private readonly ngModuleLoader: NgModuleFactoryLoader,
        private readonly injector: Injector,
        private readonly downloadStrategy: LazyViewDownloadStrategy
    ) {
    }

    public async loadLazyViewFromModule(lazyLoadableModuleId: LazyLoadableModuleId): Promise<ComponentFactory<any>> {
        let ngModuleRef: NgModuleRef<any> = this.ngModuleCache.get(lazyLoadableModuleId);

        if (!ngModuleRef) {
            await this.downloadStrategy.shouldLoad(lazyLoadableModuleId);

            const ngModuleFactory = await this.ngModuleLoader.load(lazyLoadableModuleId);

            // Note: `Injector`s are hierarchical in Angular:
            // https://angular.io/guide/hierarchical-dependency-injection

            // Instantiate the `@NgModule` with the parent `@NgModule`'s `injector`
            // This will allow the lazy `@NgModule` to use `@Injectable()`s available
            // in the parent `Injector`
            ngModuleRef = ngModuleFactory.create(this.injector);

            this.ngModuleCache.set(lazyLoadableModuleId, ngModuleRef);
        }

        // Obtain the `Injector` from the lazy as `lazyModuleInjector`, as well as the
        // `componentFactoryResolver`.
        const { injector: lazyModuleInjector, componentFactoryResolver } = ngModuleRef;

        // From the lazy `@NgModule`'s `Injector`, obtain the instance for `LAZY_VIEW_ENTRY_COMPONENT`
        // This is the `@Component` `class` which will be the root of the new component tree.
        const entryComponentClass = lazyModuleInjector.get<any>(LAZY_VIEW_ENTRY_COMPONENT);

        // With the `@Component` `class`, we can obtain the `@Component` factory.
        // This is used to 'stamp out' a new `@Component`
        return componentFactoryResolver.resolveComponentFactory<any>(entryComponentClass);
    }
}
